// Memo templates. Each is a pure function: (graph, movements, args) → string
// of markdown. Caller renders + lets user edit.
//
// Why string-templates and not a structured doc model: the audience is execs
// who want to copy-paste into email or Notion. Markdown is the lowest-common-
// denominator output that round-trips into any of those.

import { LENSES, LENS_LABEL, lensOfBusinessLine, MACROS, MACRO_LABEL, macroOfCategory, type Lens, type Macro } from "./macros.ts";
import type { ScoreMovement, TopicWithContext } from "./types.ts";

export type MemoKind =
  | "weekly-insight"
  | "monthly-shift"
  | "quarterly-thesis"
  | "experiment-hypothesis"
  | "annual-horizon";

export interface MemoTemplate {
  kind: MemoKind;
  title: string;
  cadence: string;
  description: string;
  /** True if the memo is single-topic (requires a topic_id arg). */
  topicScoped: boolean;
}

export const MEMO_TEMPLATES: MemoTemplate[] = [
  {
    kind: "weekly-insight",
    title: "Weekly insight",
    cadence: "Weekly",
    description: "Top movers, new evidence, and the so-what — one screen.",
    topicScoped: false,
  },
  {
    kind: "monthly-shift",
    title: "Monthly shift note",
    cadence: "Monthly",
    description: "What moved this month, why, and what it implies for outside × inside.",
    topicScoped: false,
  },
  {
    kind: "quarterly-thesis",
    title: "Quarterly thesis · single topic",
    cadence: "Quarterly",
    description: "Per-topic memo: thesis, score breakdown, outside→inside impact, H1→H3 backcast, experiments, kill criteria.",
    topicScoped: true,
  },
  {
    kind: "experiment-hypothesis",
    title: "Experiment hypothesis",
    cadence: "Ad hoc",
    description: "For a topic: hypothesis, success metric, smallest-viable test, decision rule.",
    topicScoped: true,
  },
  {
    kind: "annual-horizon",
    title: "Annual horizon report",
    cadence: "Annual",
    description: "Multi-topic synthesis. Per macro × lens, top theses + the experiments behind them.",
    topicScoped: false,
  },
];

export interface MemoContext {
  ctxs: TopicWithContext[];
  movements: ScoreMovement[];
  /** ISO date the memo is generated against. */
  generatedAt: string;
  /** Period scope label, for the watermark. */
  period: string;
  /** The pinned topic, if topic-scoped. */
  topic?: TopicWithContext | null;
  /** All business lines (needed for lens rollups). */
  businessLines: Array<{ id: number; key: string; name: string }>;
  /** All categories (needed for macro rollups). */
  categories: Array<{ id: number; name: string }>;
}

const fmtDate = (iso: string) => iso.slice(0, 10);
const escMd = (s: string | null | undefined) => String(s ?? "").replace(/\|/g, "\\|");

function watermark(ctx: MemoContext): string {
  return [
    `> *Generated ${fmtDate(ctx.generatedAt)} · period ${ctx.period} · ${ctx.ctxs.length} topics in scope. Re-render to refresh against latest scores.*`,
    "",
  ].join("\n");
}

function lensRollup(ctx: TopicWithContext, blKeyToLens: Map<string, Lens>): Record<Lens, number | null> {
  const buckets: Record<Lens, number[]> = { client: [], field: [], enterprise: [] };
  for (const [k, r] of Object.entries(ctx.relevance)) {
    if (r.score == null) continue;
    const lens = blKeyToLens.get(k);
    if (!lens) continue;
    buckets[lens].push(r.score);
  }
  const out: Record<Lens, number | null> = { client: null, field: null, enterprise: null };
  for (const lens of LENSES) {
    if (buckets[lens].length === 0) continue;
    out[lens] = buckets[lens].reduce((a, b) => a + b, 0) / buckets[lens].length;
  }
  return out;
}

function topicMacros(ctx: TopicWithContext): Macro[] {
  const set = new Set<Macro>();
  for (const m of ctx.memberships) set.add(macroOfCategory(m.category));
  return [...set];
}

function buildBlKeyToLens(bls: MemoContext["businessLines"]): Map<string, Lens> {
  const m = new Map<string, Lens>();
  for (const bl of bls) m.set(bl.key, lensOfBusinessLine(bl));
  return m;
}

// ---------- Weekly insight ----------
function weeklyInsight(ctx: MemoContext): string {
  const sorted = [...ctx.movements].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const ups = sorted.filter((m) => m.delta > 0).slice(0, 5);
  const downs = sorted.filter((m) => m.delta < 0).slice(0, 5);
  const newish = ctx.ctxs.filter((c) => c.topic.ai_drafted).slice(0, 5);

  const moverRow = (m: ScoreMovement, sign: string) =>
    `- **${sign}${Math.abs(m.delta)}** · ${escMd(m.topic_name)} · ${escMd(m.metric_name)} (${m.score_prev ?? "—"} → ${m.score_now})${m.justification ? ` — *${escMd(m.justification)}*` : ""}`;

  return [
    `# Weekly insight · ${fmtDate(ctx.generatedAt)}`,
    "",
    watermark(ctx),
    "## What moved",
    "",
    ups.length ? "### Rising" : "",
    ups.map((m) => moverRow(m, "↑+")).join("\n"),
    "",
    downs.length ? "### Falling" : "",
    downs.map((m) => moverRow(m, "↓−")).join("\n"),
    "",
    newish.length ? "### New on the radar" : "",
    newish.map((c) => `- ${escMd(c.topic.name)}${c.topic.h3_thesis_flag ? " · *h3 thesis*" : ""}`).join("\n"),
    "",
    "## So what",
    "",
    "_Edit this section: pick the 1–3 movers above that change a decision this week and write the ‘so what’ in plain language._",
    "",
  ].filter((line) => line !== "").join("\n");
}

// ---------- Monthly shift ----------
function monthlyShift(ctx: MemoContext): string {
  const blKeyToLens = buildBlKeyToLens(ctx.businessLines);

  // Aggregate cell movement: average movement direction across topics in each
  // (macro, lens) intersection. We don't have monthly snapshots yet, so we
  // approximate using current readiness vs. relevance gradient as a proxy.
  const cellNotes: Array<{ macro: Macro; lens: Lens; topics: TopicWithContext[]; avg: number }> = [];
  for (const macro of MACROS) {
    for (const lens of LENSES) {
      const bucket: TopicWithContext[] = [];
      const scores: number[] = [];
      for (const c of ctx.ctxs) {
        if (!topicMacros(c).includes(macro)) continue;
        const r = lensRollup(c, blKeyToLens);
        if (r[lens] == null) continue;
        bucket.push(c);
        const readiness = c.readiness?.avg_readiness_v1 ?? 0;
        scores.push((readiness + r[lens]!) / 2);
      }
      if (bucket.length === 0) continue;
      cellNotes.push({
        macro, lens,
        topics: bucket,
        avg: scores.reduce((a, b) => a + b, 0) / scores.length,
      });
    }
  }
  cellNotes.sort((a, b) => b.avg - a.avg);
  const top = cellNotes.slice(0, 3);

  const movers = [...ctx.movements].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).slice(0, 8);

  return [
    `# Monthly shift note · ${fmtDate(ctx.generatedAt)}`,
    "",
    watermark(ctx),
    "## Headline",
    "",
    "_One sentence summary — edit this._",
    "",
    "## Hottest intersections",
    "",
    top.length === 0 ? "_No intersections have enough scored topics yet to summarize._" : "",
    ...top.map((c) =>
      `- **${MACRO_LABEL[c.macro]} × ${LENS_LABEL[c.lens]}** — avg intensity ${c.avg.toFixed(2)}/3 across ${c.topics.length} topics. Topical leaders: ${c.topics.slice(0, 3).map((t) => escMd(t.topic.name)).join(", ")}.`,
    ),
    "",
    "## Top movers",
    "",
    ...movers.map((m) =>
      `- ${m.delta > 0 ? "↑" : "↓"} ${escMd(m.topic_name)} · ${escMd(m.metric_name)} ${m.score_prev ?? "—"} → ${m.score_now}${m.justification ? ` — *${escMd(m.justification)}*` : ""}`,
    ),
    "",
    "## What it implies",
    "",
    "- _Replace these bullets with what the movement implies for Client / Field / Enterprise next month._",
    "- _Flag any topic where the implied action conflicts with current priorities._",
    "",
  ].filter((l) => l !== "").join("\n");
}

// ---------- Quarterly thesis (single-topic) ----------
function quarterlyThesis(ctx: MemoContext): string {
  if (!ctx.topic) {
    return "_Pick a topic from the map and re-run this template — quarterly thesis is single-topic._";
  }
  const t = ctx.topic.topic;
  const blKeyToLens = buildBlKeyToLens(ctx.businessLines);
  const lens = lensRollup(ctx.topic, blKeyToLens);

  const readinessRows: string[] = [];
  for (const [key, score] of Object.entries(ctx.topic.scores)) {
    if (score.score == null) continue;
    readinessRows.push(`| ${escMd(key)} | ${score.score} | ${score.estimated ? "AI estimate" : "wired"} | ${escMd(score.justification ?? "")} |`);
  }

  const lensRows = LENSES.map((l) => `| ${LENS_LABEL[l]} | ${lens[l] != null ? lens[l]!.toFixed(2) : "—"} |`).join("\n");

  return [
    `# Quarterly thesis · ${escMd(t.name)}`,
    "",
    watermark(ctx),
    `**Taxonomy.** ${ctx.topic.memberships.map((m) => `${escMd(m.category.name)} › ${escMd(m.trend.name)}`).join(" · ")}`,
    "",
    "## Thesis",
    "",
    t.definition ? `${escMd(t.definition)}` : "_Definition pending — capture in Admin or via Capture._",
    "",
    "## Readiness · score breakdown",
    "",
    readinessRows.length ? "| Input | Score | Source | Why |\n| --- | --- | --- | --- |" : "_No scored readiness inputs yet._",
    ...readinessRows,
    `\n**Avg readiness:** ${ctx.topic.readiness?.avg_readiness_v1?.toFixed(2) ?? "—"} / 3`,
    "",
    "## Outside → Inside impact",
    "",
    "| Lens | Avg relevance |",
    "| --- | --- |",
    lensRows,
    "",
    "## H1 · Improve · 0–2y",
    "",
    t.recommendation ? escMd(t.recommendation) : "_What we'd do near-term to make existing offerings or processes incrementally better._",
    "",
    "## H2 · Add · 2–5y",
    "",
    t.tipping_point ? escMd(t.tipping_point) : "_What new capability or product surface this enables in the medium term._",
    "",
    "## H3 · Transform · 5–10y",
    "",
    t.milestone_signal ? escMd(t.milestone_signal) : "_The transformative milestone — what becomes possible if the trajectory holds._",
    "",
    "## Experiments to validate",
    "",
    "- _Smallest test that would meaningfully move the readiness or relevance score._",
    "- _Falsification: what observation would invalidate the thesis?_",
    "",
    "## Kill criteria",
    "",
    "- _If we observe X by date Y and the score has not moved up, we deprioritize._",
    "",
    "## Timing projection",
    "",
    `${t.h3_thesis_flag ? "Long-horizon (5–10y window)" : "Near-to-mid horizon (0–5y window)"} based on current scoring. Re-asses each quarter.`,
    "",
  ].join("\n");
}

// ---------- Experiment hypothesis ----------
function experimentHypothesis(ctx: MemoContext): string {
  if (!ctx.topic) {
    return "_Pick a topic — experiment hypothesis is single-topic._";
  }
  const t = ctx.topic.topic;
  return [
    `# Experiment hypothesis · ${escMd(t.name)}`,
    "",
    watermark(ctx),
    "## Hypothesis",
    "",
    `_If we [intervention], then [observable outcome on ${escMd(t.name)}'s readiness / relevance / adoption], because [mechanism]._`,
    "",
    "## Success metric",
    "",
    "- Primary: _One leading indicator we can measure in ≤ 8 weeks._",
    "- Secondary: _One trailing indicator that confirms over a quarter._",
    "",
    "## Smallest viable test",
    "",
    "- Scope: _The narrowest customer / region / segment that still tests the mechanism._",
    "- Duration: _Weeks, not quarters._",
    "- Cost ceiling: _$ / FTE-weeks._",
    "",
    "## Decision rule",
    "",
    "- If primary metric crosses _threshold_, we _scale to next stage_.",
    "- If primary metric stalls below _threshold_, we _pivot or stop_.",
    "- Stop-condition: _If [observation], halt regardless of metric._",
    "",
  ].join("\n");
}

// ---------- Annual horizon ----------
function annualHorizon(ctx: MemoContext): string {
  const blKeyToLens = buildBlKeyToLens(ctx.businessLines);

  // Top-line numbers across the whole portfolio.
  const totalTopics = ctx.ctxs.length;
  const h3Count = ctx.ctxs.filter((c) => c.topic.h3_thesis_flag).length;
  const transformCount = ctx.ctxs.filter((c) => c.topic.transformation_level === "transform").length;
  const avgReadiness = ctx.ctxs
    .map((c) => c.readiness?.avg_readiness_v1)
    .filter((x): x is number => x != null);
  const avgReadinessOverall = avgReadiness.length
    ? avgReadiness.reduce((a, b) => a + b, 0) / avgReadiness.length
    : null;

  // Per-macro narrative blocks.
  const macroBlocks: string[] = [];
  for (const macro of MACROS) {
    const inMacro = ctx.ctxs.filter((c) => topicMacros(c).includes(macro));
    if (inMacro.length === 0) continue;
    inMacro.sort((a, b) => (b.readiness?.avg_readiness_v1 ?? 0) - (a.readiness?.avg_readiness_v1 ?? 0));

    // Per-lens callouts inside the macro.
    const lensCallouts: string[] = [];
    for (const lens of LENSES) {
      const lensTopics = inMacro.filter((c) => {
        const r = lensRollup(c, blKeyToLens);
        return r[lens] != null;
      });
      if (lensTopics.length === 0) continue;
      lensTopics.sort((a, b) => {
        const la = lensRollup(a, blKeyToLens)[lens] ?? 0;
        const lb = lensRollup(b, blKeyToLens)[lens] ?? 0;
        return lb - la;
      });
      const top = lensTopics.slice(0, 3);
      lensCallouts.push(
        `**${LENS_LABEL[lens]}.** ${top.map((c) => escMd(c.topic.name)).join(", ")}`,
      );
    }

    macroBlocks.push(
      `## ${MACRO_LABEL[macro]}\n\n` +
      `_${inMacro.length} topics tracked · ${inMacro.filter((c) => c.topic.h3_thesis_flag).length} flagged H3._\n\n` +
      "**Headline.** _Edit: one-paragraph thesis on what's happening in this macro and why it matters this year._\n\n" +
      "**Movers.**\n\n" +
      inMacro.slice(0, 5).map((c) =>
        `- **${escMd(c.topic.name)}** — readiness ${c.readiness?.avg_readiness_v1?.toFixed(2) ?? "—"}, ${c.topic.transformation_level ?? "—"}${c.topic.h3_thesis_flag ? " · *h3*" : ""}`,
      ).join("\n") +
      "\n\n**Inside-out impact.**\n\n" + lensCallouts.map((s) => `- ${s}`).join("\n"),
    );
  }

  return [
    `# Annual horizon report · ${fmtDate(ctx.generatedAt)}`,
    "",
    watermark(ctx),
    "## Executive summary",
    "",
    "_Three-sentence summary of the year's most important shifts — edit before sending._",
    "",
    "## Portfolio at a glance",
    "",
    `- **${totalTopics}** topics tracked across Tech, Industry, Society.`,
    `- **${h3Count}** flagged H3 (long-horizon transformative).`,
    `- **${transformCount}** classified as Transform-level (vs. Improve / Add).`,
    avgReadinessOverall != null ? `- **Avg readiness:** ${avgReadinessOverall.toFixed(2)} / 3 across the portfolio.` : `- **Avg readiness:** insufficient data.`,
    "",
    macroBlocks.length ? macroBlocks.join("\n\n") : "_No scored topics yet._",
    "",
    "## What we ran this year",
    "",
    "- _List the experiments closed this year and what they taught us._",
    "- _Highlight any kills — what we stopped pursuing and why._",
    "",
    "## What's next",
    "",
    "- _The 3–5 bets we are placing for next year, with their kill criteria._",
    "- _Where we'd reallocate effort if a single shock materialized (regulatory tightening, capital crunch, etc.)._",
    "",
    "## Source attribution",
    "",
    "_This report was generated against the Trend Tracker live data on " + fmtDate(ctx.generatedAt) + ". Score breakdowns and citations available in each topic page._",
    "",
  ].filter((l) => l !== "").join("\n");
}

// ---------- Dispatch ----------
export function generateMemo(kind: MemoKind, ctx: MemoContext): string {
  switch (kind) {
    case "weekly-insight":        return weeklyInsight(ctx);
    case "monthly-shift":          return monthlyShift(ctx);
    case "quarterly-thesis":       return quarterlyThesis(ctx);
    case "experiment-hypothesis":  return experimentHypothesis(ctx);
    case "annual-horizon":         return annualHorizon(ctx);
  }
}
